import { COVERTYPE } from "@/constants/common.constants";
import { FACT_FIND_ANSWER_MAP } from "@/constants/factfind.constants";
import { TENANTS_CONTENTS } from "@/constants/questionset.constants";
import store from "@/store/index";

const numberWords = [
  // Buildings:
  { word: "FiveHundredThousand", num: 500000 },
  { word: "OneMillion", num: 1000000 },
  // Contents:
  { word: "TenThousand", num: 10000 },
  { word: "TwentyThousand", num: 20000 },
  { word: "ThirtyThousand", num: 30000 },
  { word: "FortyThousand", num: 40000 },
  { word: "FiftyThousand", num: 50000 },
  { word: "SeventyFiveThousand", num: 75000 },
  { word: "OneHundredThousand", num: 100000 },
  // Excess:
  { word: "Fifty", num: 50 },
  { word: "OneHundred", num: 100 },
  { word: "OneHundredAndFifty", num: 150 },
  { word: "TwoHundred", num: 200 },
  { word: "TwoHundredAndFifty", num: 250 },
  { word: "ThreeHundred", num: 300 },
  { word: "ThreeHundredAndFifty", num: 350 },
  { word: "FourHundred", num: 400 },
  { word: "FourHundredAndFifty", num: 450 },
  { word: "FiveHundred", num: 500 },
  //No Claims:
  { word: "Zero", num: 0 },
  { word: "One", num: 1 },
  { word: "Two", num: 2 },
  { word: "Three", num: 3 },
  { word: "Four", num: 4 },
  { word: "Five", num: 5 },
  { word: "Six", num: 6 },
  { word: "Seven", num: 7 },
  { word: "Eight", num: 8 },
  { word: "NinePlus", num: "9+" },
  { word: "Ten", num: "10" },
  { word: "TenPlus", num: "11+" },
];

const periodWords = [
  {
    period: "30 days",
    word: "ThirtyDays",
  },
  {
    period: "60 days",
    word: "SixtyDays",
  },
  {
    period: "180 days",
    word: "OneHundredEightyDays",
  },
  {
    period: "12 months",
    word: "TwelveMonths",
  },
  {
    period: "24 months",
    word: "TwentyFourMonths",
  },
];

export const findAnswer = (quoteRequest, interfaceKey) => {
  const answers = quoteRequest.Answers;
  if (!answers) return;
  const answerObject = answers.find((a) => a.InterfaceKey === interfaceKey);
  if (!answerObject) return;
  return answerObject.Value;
};

export const findAnswerObj = (quoteRequest, interfaceKey) => {
  const answers = quoteRequest.Answers;
  if (!answers) return;
  const answerObject = answers.find((a) => a.InterfaceKey === interfaceKey);
  if (!answerObject) return;
  return answerObject;
};

export const wordsToNumbers = (wordValue) => {
  if (!wordValue) return 0;
  const match = numberWords.find((w) => w.word === wordValue);
  if (!match) return 0;
  return match.num;
};

export const numbersToWords = (numberValue) => {
  if (numberValue == null) return "";

  const match = numberWords.find((n) => n.num === numberValue);

  if (!match) return "";

  return match.word;
};

export const wordsToBools = (wordValue) => {
  return wordValue === "True";
};

export const wordToPeriod = (word) => {
  if (!word) {
    return;
  }

  const match = periodWords.find((w) => w.word === word);

  if (!match) {
    return;
  }

  return match.period;
};

export const formatCoverType = (answerKey) => {
  return coverType.find((ct) => ct.key === answerKey)?.description;
};

const coverType = [
  {
    key: COVERTYPE.BuildingsAndContents,
    description: "Buildings & Contents",
  },
  { key: COVERTYPE.BuildingsOnly, description: "Buildings only" },
  { key: COVERTYPE.ContentsOnly, description: "Contents only" },
];

export const includesContents = (coverTypeQuestion) => {
  return (
    coverTypeQuestion?.Value === COVERTYPE.BuildingsAndContents ||
    coverTypeQuestion?.Value === COVERTYPE.ContentsOnly
  );
};

export const includesBuildings = (coverTypeQuestion) => {
  return (
    coverTypeQuestion?.Value === COVERTYPE.BuildingsAndContents ||
    coverTypeQuestion?.Value === COVERTYPE.BuildingsOnly
  );
};

export const findNumberOfPpItems = (quoteRequest) => {
  const answers = quoteRequest?.Answers;
  const ppAnswerObjects = answers?.filter(
    (a) =>
      a.InterfaceKey === "PPItemDescription" ||
      a.InterfaceKey === "SpecifiedItemDescription"
  );
  return ppAnswerObjects?.length ?? 0;
};

export const formatPpItems = (quoteRequest) => {
  const answers = quoteRequest.Answers;
  const ppAnswerObjects = answers?.filter(
    (a) =>
      a.InterfaceKey === "PPItemDescription" ||
      a.InterfaceKey === "SpecifiedItemDescription" ||
      a.InterfaceKey === "PPItemType" ||
      a.InterfaceKey === "SpecifiedItemType" ||
      a.InterfaceKey === "PPItemValue" ||
      a.InterfaceKey === "SpecifiedItemValue"
  );

  const groupedPpItems = ppAnswerObjects?.reduce((groups, item) => {
    const group = groups[item.RepeatingQuestionSetIndex] || [];
    group.push(item);
    groups[item.RepeatingQuestionSetIndex] = group;
    return groups;
  }, []);

  return groupedPpItems;
};

export const getRepeatingGroups = (answers, keys) => {
  const obj = answers
    .filter((answer) => keys.includes(answer.InterfaceKey))
    .reduce((groups, answer) => {
      const { RepeatingQuestionSetIndex } = answer;

      if (!groups[RepeatingQuestionSetIndex]) {
        groups[RepeatingQuestionSetIndex] = [];
      }
      groups[RepeatingQuestionSetIndex].push(answer);

      return groups;
    }, {});
  return Object.values(obj);
};

export const formatRepeatingGroupsCount = (
  groups,
  defaultFormat = "Not specified"
) => {
  if (groups.length === 0) {
    return defaultFormat;
  }

  const count = groups.length;

  return `${count} item${count !== 1 ? "s" : ""}`;
};

export const formAnswersToApiAnswers = (formObject, productGroup) => {
  const homeAddOn = formObject.additionalCoverOptions.find(
    (item) => item.code === "he"
  );

  const legalAddOn = formObject.additionalCoverOptions.find(
    (item) => item.code === "le"
  );

  const accidentalDamage = formObject.additionalCoverOptions.find(
    (item) => item.code === "ad"
  );

  // Add all B&C answers, we will remove non-applicables later in preparedAnswers
  let answers = [
    {
      InterfaceKey: "HomeEmergency",
      Value: homeAddOn ? homeAddOn.isAdded : false,
    },
    {
      InterfaceKey: "LegalExpenses",
      Value: legalAddOn ? legalAddOn.isAdded : false,
    },
    {
      InterfaceKey: "BuildingsCoverLevel",
      Value: numbersToWords(formObject.buildingsCover.amount),
    },
    {
      InterfaceKey: "ExcessBuildings",
      Value: numbersToWords(formObject.buildingsCover.excess),
    },
    {
      InterfaceKey: "ContentsCoverLevel",
      Value: numbersToWords(formObject.contentsCover.amount),
    },
    {
      InterfaceKey: "ExcessContents",
      Value: numbersToWords(formObject.contentsCover.excess),
    },
  ];

  // Check whether there are PersonalPossessions or ContentsItems to specify, and if so add the appropriate interface keys.
  if (formObject.personalPossessions.length > 0) {
    if (
      formObject.personalPossessions.find(
        (ppCategory) => ppCategory.interfaceKeyPrefix === "PP"
      )
    ) {
      answers.push({
        InterfaceKey: "PersonalPossessionsToSpecify",
        Value: true,
      });
    }
    if (
      formObject.personalPossessions.find(
        (ppCategory) => ppCategory.interfaceKeyPrefix === "Specified"
      )
    ) {
      answers.push({
        InterfaceKey: "ContentsItemsToSpecify",
        Value: true,
      });
    }

    // Add the individual specified items.
    // The repeatingQuestionSetIndex is a globally incrementing counter that needs to be incremented for anything with a Repeating Question Set Index (e.g. PP's and Specified Items)
    formObject.personalPossessions.forEach((ppCategory) => {
      ppCategory.items.forEach((item) => {
        answers.push(
          {
            InterfaceKey: `${ppCategory.interfaceKeyPrefix}ItemType`,
            Value: item.itemType,
            RepeatingQuestionSetIndex:
              store.getters["questions/repeatingQuestionSetIndex"],
          },
          {
            InterfaceKey: `${ppCategory.interfaceKeyPrefix}ItemDescription`,
            Value: item.description,
            RepeatingQuestionSetIndex:
              store.getters["questions/repeatingQuestionSetIndex"],
          },
          {
            InterfaceKey: `${ppCategory.interfaceKeyPrefix}ItemValue`,
            Value: item.value,
            RepeatingQuestionSetIndex:
              store.getters["questions/repeatingQuestionSetIndex"],
          }
        );
        store.dispatch("questions/increaseRepeatingQuestionSetIndex");
      });
    });
  }

  // Add in the unspecified PP amount if applicable
  if (formObject.unspecifiedPpAmount) {
    answers.push({
      InterfaceKey: "PersonalPossessionsTotalAmount",
      Value: formObject.unspecifiedPpAmount,
    });
  }
  if (productGroup === TENANTS_CONTENTS) {
    answers.push({
      InterfaceKey: "AccidentalDamageContents",
      Value: accidentalDamage ? accidentalDamage.isAdded : false,
    });
    if (formObject.personalPossessions.length > 0) {
      if (
        formObject.personalPossessions.find(
          (ppCategory) => ppCategory.interfaceKeyPrefix === "PP"
        )
      ) {
        answers.push({
          InterfaceKey: "PersonalPossessionsOver2500",
          Value: true,
        });
      }
    }

    // Check and push "ContentsItemsToSpecify" if not present
    if (
      !answers.some(
        (answer) => answer.InterfaceKey === "ContentsItemsToSpecify"
      )
    ) {
      answers.push({
        InterfaceKey: "ContentsItemsToSpecify",
        Value: false,
      });
    }

    // Check and push "PersonalPossessionsOver2500" if not present
    if (
      !answers.some(
        (answer) => answer.InterfaceKey === "PersonalPossessionsOver2500"
      )
    ) {
      answers.push({
        InterfaceKey: "PersonalPossessionsOver2500",
        Value: false,
      });
    }
  }

  return answers;
};

export const factFindToApiAnswers = (factFind) => {
  let answers = [];
  const keys = Object.keys(factFind);

  keys.forEach((k) => {
    const interfaceKey = FACT_FIND_ANSWER_MAP[k];
    const value = factFind[k];
    answers.push({
      InterfaceKey: interfaceKey,
      Value: value,
    });
  });

  const keysToRemove = [];

  answers.forEach((a) => {
    // Convert payment answers from bool to "Monthly" or "Annual(ly)"
    // Remove answers to questions that are currently hidden
    if (a.InterfaceKey === "FactFindPaymentFrequency") {
      a.Value = a.Value ? "Monthly" : "Annually";
    } else if (a.InterfaceKey === "FactFindPaymentPreference") {
      a.Value = a.Value ? "Monthly" : "Annual";
    } else if (a.InterfaceKey === "FactFindCurrentPolicyInPlace" && !a.Value) {
      keysToRemove.push(
        "FactFindRenewalDate",
        "FactFindCurrentInsurer",
        "FactFindCurrentCover",
        "FactFindBuildingsCoverAmount",
        "FactFindContentsCoverAmount",
        "FactFindPaymentFrequency",
        "FactFindCurrentPremium"
      );
    } else if (
      a.InterfaceKey === "FactFindImportanceBuildingsCover" &&
      !a.Value
    ) {
      keysToRemove.push("FactFindImportanceAccidentalBuildings");
    } else if (
      a.InterfaceKey === "FactFindImportanceContentsCover" &&
      !a.Value
    ) {
      keysToRemove.push(
        "FactFindImportanceAccidentalContents",
        "FactFindImportancePersonalPossessions"
      );
    }
  });

  // Remove any answers which appear in the keysToRemove collection
  answers = answers.filter((ans) => !keysToRemove.includes(ans.InterfaceKey));
  return answers;
};

export const apiAnswerToFactFindValue = (answer) => {
  // For payment answers, convert "Monthly" to true;
  if (
    answer.InterfaceKey === "FactFindPaymentFrequency" ||
    answer.InterfaceKey === "FactFindPaymentPreference"
  )
    return answer.Value === "Monthly";

  if (answer.InterfaceKey === "FactFindRenewalDate")
    return parseApiDate(answer.Value);

  // Convert "True"/"False" strings to bools
  if (answer.Value === "True" || answer.Value === "False")
    return answer.Value === "True";

  if (answer.Value.includes(".00")) return answer.Value.replace(".00", "");

  // Everything else, pass through as-is
  return answer.Value;
};

export const parseApiDate = (apiDate) => {
  return apiDate.replace("00:00:00", "").trim();
};

export const parseApiAnswer = (apiAnswer) => {
  if (apiAnswer.Value === "True" || apiAnswer.Value === "False") {
    return apiAnswer.Value.toLowerCase();
  } else if (
    apiAnswer.InterfaceKey === "PolicyStartDate" ||
    apiAnswer.InterfaceKey === "MortgageStartDate"
  ) {
    return parseApiDate(apiAnswer.Value);
  } else {
    return apiAnswer.Value;
  }
};

export const getDirectOrDeepValue = (question) => {
  return typeof question.Value === "string"
    ? question.Value
    : question.Value?.Value;
};

export const formatAnswerByOptions = (value, options) => {
  if (!value) {
    return;
  }

  const match = options.find((o) => o.value === value);

  if (!match) {
    return;
  }

  return match.text;
};
